import NavTabs from "@/components/NavTabs/NavTabs";
import { Avatar } from "@/components/ui";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { useSetPageTitle } from "@/context/PageTitleContext";
import BoxEventiInsegnamento from "@/pages/courses/components/BoxEventiInsegnamento";
import ZoomLinksBox from "@/pages/courses/components/ZoomLinksBox";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getEntity } from "@/shared/reducers/entities/edo-course.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { useMediaQuery } from "@/shared/util/useMediaQuery";
import {
    ClipboardList,
    FolderOpen,
    LayoutDashboard,
    Calendar,
    Users,
    GraduationCap,
} from "lucide-react";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";

export const CourseDetailRoutes = () => {
    const dispatch = useAppDispatch();
    const params = useParams<{ id: string }>();
    const isMediaSm = useMediaQuery("(max-width: 640px)");
    const { entity: course } = useAppSelector<EntityState<IEdoCourse>>((state) => state.edoCourse);
    const { account } = useAppSelector<AuthenticationState>(
        (state) => state.authentication
    );

    const courseTitle = course?.subject?.name ?? "Corso";
    const courseSubtitle = course?.class?.year
        ? `${String(course.class.year.year)}° ${course.class.section ?? ""} – ${course.class.school?.type ?? ""} ${course.class.school?.subtype ?? ""}`.trim()
        : "Dettaglio corso";
    useSetPageTitle(courseTitle, courseSubtitle);

    useEffect(() => {
        dispatch(getEntity(params.id));
    }, [dispatch, params.id]);

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                    <div className="lg:col-span-12">
                        <div className="surface overflow-hidden rounded-t-2xl">
                            {/* Hero */}
                            <div
                                className="flex h-48 flex-col justify-center bg-cover bg-center bg-no-repeat sm:h-52"
                                style={{
                                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/class.jpg")',
                                }}
                            >
                                <div className="flex flex-row items-center gap-5 p-5">
                                    <div className="flex shrink-0 flex-col items-center justify-center">
                                        {course?.id ? (
                                            <Avatar
                                                className="h-24 w-24 border-2 border-white/80"
                                                src="/package/logo.png"
                                            />
                                        ) : (
                                            <div className="h-24 w-24 animate-pulse rounded-full bg-[var(--color-mockup-border)]" />
                                        )}
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        {course?.subject?.name ? (
                                            <h1 className="mb-1 text-xl font-semibold tracking-tight text-white sm:text-2xl">
                                                {course.subject.name}
                                            </h1>
                                        ) : (
                                            <div className="mb-1 h-7 w-32 animate-pulse rounded-sm bg-[var(--color-mockup-border)]" />
                                        )}
                                        {course?.class && course.class.year ? (
                                            <p className="text-sm font-medium text-white/95 sm:text-base">
                                                {String(course.class.year.year)}° {course.class.section}
                                                {" – "}
                                                {course.class.school.type} {course.class.school.subtype}
                                                {course.class.school.name ? ` ${course.class.school.name}` : ""}
                                            </p>
                                        ) : (
                                            <div className="h-5 w-48 animate-pulse rounded-sm bg-[var(--color-mockup-border)]" />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <NavTabs
                                centered={!isMediaSm}
                                variant={isMediaSm ? "scrollable" : "standard"}
                                scrollButtons="auto"
                                tabs={[
                                    {
                                        label: "Bacheca",
                                        icon: <LayoutDashboard className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/`,
                                    },
                                    {
                                        label: "Materiale",
                                        icon: <FolderOpen className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/materials`,
                                        pattern: `/courses/${params.id}/materials/*`,
                                    },
                                    {
                                        label: "Test",
                                        icon: <ClipboardList className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/quizzes`,
                                        pattern: `/courses/${params.id}/quizzes/*`,
                                    },
                                    {
                                        label: "Eventi",
                                        icon: <Calendar className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/events`,
                                        pattern: `/courses/${params.id}/events/*`,
                                        hidden: !hasAnyAuthority(account.roles, [
                                            AUTHORITIES.ROLE_TEACHER,
                                            AUTHORITIES.ROLE_ADMIN,
                                        ]),
                                    },
                                    {
                                        label: "Studenti",
                                        icon: <Users className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/students`,
                                        pattern: `/courses/${params.id}/students/*`,
                                        hidden: !hasAnyAuthority(account.roles, [
                                            AUTHORITIES.ROLE_TEACHER,
                                            AUTHORITIES.ROLE_ADMIN,
                                        ]),
                                    },
                                    {
                                        label: "Docenti",
                                        icon: <GraduationCap className="h-5 w-5" />,
                                        iconPosition: "start",
                                        href: `/courses/${params.id}/teachers`,
                                        pattern: `/courses/${params.id}/teachers/*`,
                                        hidden: !hasAnyAuthority(account.roles, [
                                            AUTHORITIES.ROLE_TEACHER,
                                            AUTHORITIES.ROLE_ADMIN,
                                        ]),
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-8 xl:col-span-9">
                        <Outlet />
                    </div>
                    <div className="space-y-5 lg:col-span-4 xl:col-span-3">
                        <ZoomLinksBox class={course?.class} />
                        <BoxEventiInsegnamento insegnamento={course} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseDetailRoutes;
